import { SearchableSelect } from "@/components/ui/searchable-select";
import { ListEmpty } from "@/components/patterns";
import React, { useEffect, useState } from "react";
import { PageHeader, StatusPill, ViewsPanel } from "@/components/patterns";
import { FilterBar } from "@/components/filters/FilterBar";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { parseCsvToObjects } from "@/lib/csv";
import { Percent as FilterGstIcon, Ruler as FilterUnitIcon } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Package, Plus, Edit, Trash2, Loader2, RefreshCw, Search, X } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { ProductRecord, MasterLookupRecord } from "@/types";
import { AttributesDialog, ProductOptionsPanel } from "./ProductOptions";

const GST_RATES = [0, 5, 12, 18, 28];

const EMPTY_FORM = {
  name: "",
  code: "",
  category: "",
  description: "",
  unit: "NOS",
  unit_price: "",
  cost_price: "",
  gst_rate: 18,
  hsn_sac_code: "",
  status: true,
};

const selectClass =
  "h-9 sm:h-10 w-full rounded-md border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

const PRODUCT_FILTER_DEFAULTS = { gst: "all", unit: "all" };
const IMPORT_HELP = "name, code, unit_price (required); category, unit, cost_price, gst_rate, hsn_sac_code, description (optional)";

export function ProductsPage() {
  const [masterUnits, setMasterUnits] = useState<MasterLookupRecord[]>([]);
  const [masterCategories, setMasterCategories] = useState<MasterLookupRecord[]>([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE" | "LOW">("ACTIVE");
  const [stats, setStats] = useState<{ active: number; inactive: number; margin?: { average_pct: number | null; low_count: number } } | null>(null);
  const [catCounts, setCatCounts] = useState<Record<string, number>>({});
  const productFilters = usePersistentFilters("products", PRODUCT_FILTER_DEFAULTS);
  const [facets, setFacets] = useState<{ units: { value: string; count: number }[]; gst_rates: { value: string; count: number }[] }>({ units: [], gst_rates: [] });
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ ok: number; errors: { row: number; reason: string }[] } | null>(null);
  const [selected, setSelected] = useState<ProductRecord | null>(null);
  const [attributesOpen, setAttributesOpen] = useState(false);
  const [usedIn, setUsedIn] = useState<{ open_quotes: number; open_deals: number } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // null = closed, "new" = add, record = edit
  const [dialogItem, setDialogItem] = useState<ProductRecord | "new" | null>(null);
  const [deleteItem, setDeleteItem] = useState<ProductRecord | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("product.create");
  const canUpdate = hasPermission("product.update");
  const canDelete = hasPermission("product.delete");
  const canViewCost = hasPermission("product.view_cost");

  useEffect(() => {
    Promise.all([
      api.get("/api/MasterLookup/ByCategory/PRODUCT_UNIT"),
      api.get("/api/MasterLookup/ByCategory/PRODUCT_CATEGORY"),
    ])
      .then(([unitRes, catRes]) => {
        setMasterUnits(unwrapArray<MasterLookupRecord>(unitRes));
        setMasterCategories(unwrapArray<MasterLookupRecord>(catRes));
      })
      .catch(() => undefined);
  }, []);

  const list = usePagedList<ProductRecord>({
    deps: [search, categoryFilter, statusFilter, productFilters.values],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      if (categoryFilter !== "ALL") qs.set("category", categoryFilter);
      if (statusFilter === "ACTIVE" || statusFilter === "INACTIVE") qs.set("status", statusFilter.toLowerCase());
      if (productFilters.values.gst !== "all") qs.set("gst_rate", productFilters.values.gst);
      if (productFilters.values.unit !== "all") qs.set("unit", productFilters.values.unit);
      try {
        return unwrapPage<ProductRecord>(await api.get(`/api/Product/${statusFilter === "LOW" ? "LowMargin" : "List"}?${qs}`));
      } catch (err: any) {
        throw err;
      }
    },
  });
  // Counts beside the panel views and categories, refreshed whenever the list is
  useEffect(() => {
    api.get("/api/Product/Stats").then((r) => setStats(unwrapObject(r))).catch(() => undefined);
    api
      .get("/api/Product/FilterOptions")
      .then((r) => {
        const d = unwrapObject<{ categories: { value: string; count: number }[]; units: { value: string; count: number }[]; gst_rates: { value: string; count: number }[] }>(r);
        setCatCounts(Object.fromEntries(d.categories.map((c) => [c.value, c.count])));
        setFacets({ units: d.units || [], gst_rates: d.gst_rates || [] });
      })
      .catch(() => undefined);
  }, [list.total]);

  useEffect(() => {
    setUsedIn(null);
    if (selected) api.get(`/api/Product/UsedIn/${selected.id}`).then((r) => setUsedIn(unwrapObject(r))).catch(() => undefined);
  }, [selected?.id]);

  const products = list.rows;
  const loading = list.loading;
  const loadData = list.reload;

  const categories = Array.from(
    new Set([
      ...products.map((p) => p.category).filter((c): c is string => !!c),
      ...masterCategories.map((c) => c.name),
    ])
  ).sort();

  // The API already applied search, category and status
  const filtered = products;

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setDialogItem("new");
  };

  const openEdit = (p: ProductRecord) => {
    setForm({
      name: p.name,
      code: p.code,
      category: p.category || "",
      description: p.description || "",
      unit: p.unit || "NOS",
      unit_price: String(Number(p.unit_price)),
      cost_price: p.cost_price !== null && p.cost_price !== undefined ? String(Number(p.cost_price)) : "",
      gst_rate: Number(p.gst_rate),
      hsn_sac_code: p.hsn_sac_code || "",
      status: p.status !== false,
    });
    setDialogItem(p);
  };

  // Live margin preview in the dialog
  const previewMargin = (() => {
    const price = Number(form.unit_price);
    const cost = Number(form.cost_price);
    if (!form.cost_price || !price) return null;
    return ((price - cost) / price) * 100;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      error("Validation Error", "Name and code are required");
      return;
    }
    if (form.unit_price === "" || Number(form.unit_price) < 0) {
      error("Validation Error", "Enter a valid selling price");
      return;
    }

    const payload: Record<string, any> = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      category: form.category.trim() || undefined,
      description: form.description.trim() || undefined,
      unit: form.unit,
      unit_price: Number(form.unit_price),
      gst_rate: Number(form.gst_rate),
      hsn_sac_code: form.hsn_sac_code.trim() || undefined,
      status: form.status,
    };
    // Only users who can see cost send it (null clears it); others leave the stored cost untouched
    if (canViewCost) {
      payload.cost_price = form.cost_price === "" ? null : Number(form.cost_price);
    }

    setActionLoading(true);
    try {
      if (dialogItem === "new") {
        await api.post("/api/Product/Insert", payload);
        success("Product Created", "Product added to the catalog");
      } else if (dialogItem) {
        await api.put(`/api/Product/Update/${dialogItem.id}`, payload);
        success("Product Updated", "Product saved successfully");
      }
      setDialogItem(null);
      setForm(EMPTY_FORM);
      await loadData();
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to save product");
    } finally {
      setActionLoading(false);
    }
  };

  const runImport = async (file?: File | null) => {
    if (!file) return;
    setImporting(true);
    const errors: { row: number; reason: string }[] = [];
    let ok = 0;
    try {
      const rows = parseCsvToObjects(await file.text());
      if (!rows.length) throw new Error("The file has no rows");
      if (rows.length > 500) throw new Error("Import at most 500 products at a time");
      for (let i = 0; i < rows.length; i++) {
        const r = Object.fromEntries(Object.entries(rows[i]!).map(([k, v]) => [k.trim().toLowerCase(), String(v ?? "").trim()]));
        try {
          if (!r.name || !r.code || r.unit_price === "" || Number.isNaN(Number(r.unit_price))) throw new Error("name, code and a numeric unit_price are required");
          const payload: Record<string, any> = {
            name: r.name,
            code: r.code.toUpperCase(),
            unit_price: Number(r.unit_price),
            category: r.category || undefined,
            unit: (r.unit || "NOS").toUpperCase(),
            gst_rate: r.gst_rate === "" || r.gst_rate === undefined ? 18 : Number(r.gst_rate),
            hsn_sac_code: r.hsn_sac_code || undefined,
            description: r.description || undefined,
            status: true,
          };
          if (canViewCost && r.cost_price) payload.cost_price = Number(r.cost_price);
          await api.post("/api/Product/Insert", payload);
          ok++;
        } catch (e: any) {
          errors.push({ row: i + 2, reason: e?.message || "Could not add" });
        }
      }
      setImportResult({ ok, errors });
      if (ok) await loadData();
    } catch (e: any) {
      error("Could not import", e?.message || "Try again");
    } finally {
      setImporting(false);
    }
  };

  const toggleActive = async (p: ProductRecord) => {
    setActionLoading(true);
    try {
      const active = p.status !== false;
      await api.put(`/api/Product/Update/${p.id}`, {
        name: p.name,
        code: p.code,
        category: p.category || undefined,
        description: p.description || undefined,
        unit: p.unit,
        unit_price: Number(p.unit_price),
        gst_rate: Number(p.gst_rate),
        hsn_sac_code: p.hsn_sac_code || undefined,
        status: !active,
      });
      success(active ? "Product deactivated" : "Product activated", active ? "It no longer shows in the deal and quote pickers." : "It can be used on deals and quotes again.");
      setSelected(null);
      await loadData();
    } catch (err: any) {
      error("Could not change the product", err.message || "Try again");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    try {
      const res: any = await api.delete(`/api/Product/Delete/${deleteItem.id}`);
      success("Product Removed", res?.Message || "Product deleted successfully");
      setDeleteItem(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete product");
    } finally {
      setActionLoading(false);
    }
  };

  const columnCount = 6 + (canViewCost ? 2 : 0);

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Products"
        subtitle="Catalogue"
        active={statusFilter}
        onSelect={(k) => setStatusFilter(k as typeof statusFilter)}
        groups={[
          {
            items: [
              { key: "ACTIVE", label: "Active", count: stats?.active },
              { key: "INACTIVE", label: "Inactive", count: stats?.inactive },
              { key: "LOW", label: "Low margin (under 10%)", count: stats?.margin?.low_count, tone: "warning", hidden: !canViewCost },
              { key: "ALL", label: "All products", count: stats ? stats.active + stats.inactive : undefined },
            ],
          },
        ]}
        footer={
          masterCategories.length > 0 ? (
            <div className="mt-3 flex flex-col gap-0.5">
              <div className="px-2.5 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Category</div>
              {[{ code: "ALL", name: "All categories" }, ...masterCategories].map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setCategoryFilter(c.code === "ALL" ? "ALL" : c.name)}
                  className={`flex h-8 items-center gap-2 rounded-md px-2.5 text-left text-[13px] ${
                    (c.code === "ALL" ? "ALL" : c.name) === categoryFilter ? "bg-card font-semibold text-foreground shadow-[0_0_0_1px_var(--border)]" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <span className="truncate">{c.name}</span>
                  {c.code !== "ALL" && catCounts[c.name] !== undefined && <span className="ml-auto font-mono text-[11.5px]">{catCounts[c.name]}</span>}
                </button>
              ))}
            </div>
          ) : undefined
        }
      />

      <PageHeader
        title="Products"
        summary={
          <>
            {list.total}
            {canViewCost && stats?.margin?.average_pct != null && <span className="font-normal text-muted-foreground"> · average margin {stats.margin.average_pct}%</span>}
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={loadData} disabled={loading}>
              <RefreshCw className={`size-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {hasPermission("product.update") && (
              <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={() => setAttributesOpen(true)}>
                Attributes
              </Button>
            )}
            {canCreate && (
              <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={() => (setImportResult(null), setImportOpen(true))}>
                Import
              </Button>
            )}
            {canCreate && (
              <Button size="sm" onClick={openAdd}>
                <Plus className="size-4 mr-1.5" />
                Add product
              </Button>
            )}
          </>
        }
      />

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search by name, code or HSN/SAC…" }}
        fields={[
          { name: "gst", label: "GST rate", icon: FilterGstIcon, type: "select", options: [{ value: "all", label: "Any GST rate" }, ...facets.gst_rates.map((g) => ({ value: g.value, label: `${Number(g.value)}%`, count: g.count }))], defaultValue: "all" },
          { name: "unit", label: "Unit", icon: FilterUnitIcon, type: "select", options: [{ value: "all", label: "Any unit" }, ...facets.units.map((u) => ({ value: u.value.toUpperCase(), label: u.value, count: u.count }))], defaultValue: "all" },
        ]}
        values={productFilters.values}
        onChange={(name, value) => productFilters.set(name as keyof typeof PRODUCT_FILTER_DEFAULTS, value)}
        onReset={productFilters.reset}
        shown={list.total}
        noun="products"
      />

      {/* Table, with the selected product docked beside it */}
      <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
      {loading && products.length === 0 ? (
        <TableSkeleton rows={6} columns={columnCount} />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Price / Unit</TableHead>
                {canViewCost && <TableHead className="text-right">Cost</TableHead>}
                {canViewCost && <TableHead className="text-right">Margin</TableHead>}
                <TableHead className="text-right">GST</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => {
                const margin = p.margin_pct !== null && p.margin_pct !== undefined ? Number(p.margin_pct) : null;
                return (
                  <TableRow key={p.id} onClick={() => setSelected(p)} className={`cursor-pointer ${selected?.id === p.id ? "bg-accent/40" : ""}`}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                          <Package className="size-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-foreground">{p.name}</div>
                          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <code className="font-mono">{p.code}</code>
                            {p.hsn_sac_code && <span>· HSN/SAC {p.hsn_sac_code}</span>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.category || "-"}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <span className="font-semibold text-sm">{formatINR(p.unit_price)}</span>
                      <span className="text-xs text-muted-foreground"> / {p.unit}</span>
                    </TableCell>
                    {canViewCost && (
                      <TableCell className="text-right text-sm whitespace-nowrap">
                        {p.cost_price !== null && p.cost_price !== undefined ? formatINR(p.cost_price) : "-"}
                      </TableCell>
                    )}
                    {canViewCost && (
                      <TableCell className="text-right">
                        {margin === null ? (
                          <span className="text-sm text-muted-foreground">-</span>
                        ) : (
                          <Badge variant={margin < 0 ? "destructive" : margin < 15 ? "warning" : "success"}>
                            {margin.toFixed(1)}%
                          </Badge>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right text-sm">{Number(p.gst_rate)}%</TableCell>
                    <TableCell>
                      <Badge variant={p.status !== false ? "success" : "secondary"}>
                        {p.status !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {canUpdate && (
                        <Button variant="ghost" size="icon-sm" onClick={() => openEdit(p)} title="Edit Product">
                          <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setDeleteItem(p)}
                          title="Delete Product"
                        >
                          <Trash2 className="size-4 text-danger hover:text-danger" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} className="p-0">
                    <ListEmpty
                      noun="products" error={list.error} onRetry={list.reload}
                      icon={Package}
                      filtered={!!search.trim() || categoryFilter !== "ALL" || statusFilter !== "ACTIVE" || productFilters.activeCount > 0}
                      onClear={() => {
                        setSearch("");
                        setCategoryFilter("ALL");
                        setStatusFilter("ACTIVE");
                        productFilters.reset();
                      }}
                      createLabel="+ New product"
                      onCreate={canCreate ? openAdd : undefined}
                      firstUse="Products are what you quote: add each item with its price and tax."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}
      </div>
      {selected && (
        <aside aria-label="Product details" className="hidden w-[300px] shrink-0 flex-col gap-3.5 rounded-lg border border-border bg-card p-4 lg:flex">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <code className="font-mono text-xs text-muted-foreground">{selected.code}</code>
              <h2 className="text-[17px] font-semibold leading-snug">{selected.name}</h2>
              <span className="text-xs text-muted-foreground">{selected.category || "No category"}</span>
            </div>
            <Button variant="outline" size="icon-sm" aria-label="Close panel" onClick={() => setSelected(null)}>
              <X className="size-4" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-panel px-2.5 py-2">
              <div className="text-[11px] text-muted-foreground">Unit price</div>
              <div className="font-mono text-[14px] font-semibold">{formatINR(selected.unit_price)} / {selected.unit}</div>
            </div>
            {canViewCost && selected.margin_pct != null && (
              <div className="rounded-lg bg-panel px-2.5 py-2">
                <div className="text-[11px] text-muted-foreground">Margin</div>
                <div className={`font-mono text-[14px] font-semibold ${Number(selected.margin_pct) < 10 ? "text-warning" : ""}`}>{Number(selected.margin_pct).toFixed(1)}%</div>
              </div>
            )}
          </div>
          <div className="flex flex-col text-[13px]">
            {([
              ["HSN / SAC", selected.hsn_sac_code],
              ["GST rate", `${Number(selected.gst_rate)}%`],
              ["Unit", selected.unit],
              ...(canViewCost && selected.cost_price != null ? [["Cost price", formatINR(selected.cost_price)]] : []),
            ] as [string, string | undefined][]).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-border/60 py-1.5 last:border-b-0">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-medium">{v || "—"}</span>
              </div>
            ))}
          </div>
          {canViewCost && selected.cost_price != null && Number(selected.unit_price) > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] text-muted-foreground">Price against cost (per {selected.unit.toLowerCase()})</span>
              <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label={`Cost ${formatINR(selected.cost_price)} of a price of ${formatINR(selected.unit_price)}`}>
                <span className="h-full bg-primary" style={{ width: `${Math.min(100, Math.max(0, (Number(selected.cost_price) / Number(selected.unit_price)) * 100))}%` }} />
                {Number(selected.cost_price) < Number(selected.unit_price) && <span className="h-full flex-1 bg-gold" />}
              </div>
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>Cost {formatINR(selected.cost_price)}</span>
                <span>Margin {formatINR(Number(selected.unit_price) - Number(selected.cost_price))}</span>
              </div>
            </div>
          )}
          {selected.description && <p className="text-[13px] text-foreground/90">{selected.description}</p>}
          <ProductOptionsPanel productId={selected.id} unit={selected.unit} canEdit={hasPermission("product.update")} />
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-muted-foreground">Used in</span>
            <span className="font-medium">{usedIn ? `${usedIn.open_quotes} open ${usedIn.open_quotes === 1 ? "quote" : "quotes"} · ${usedIn.open_deals} open ${usedIn.open_deals === 1 ? "deal" : "deals"}` : "…"}</span>
          </div>
          <div className="mt-auto flex items-center justify-between gap-2">
            <StatusPill tone={selected.status !== false ? "success" : "neutral"}>{selected.status !== false ? "Active" : "Inactive"}</StatusPill>
            <div className="flex gap-2">
              {canUpdate && (
                <Button size="sm" variant="outline" disabled={actionLoading} onClick={() => toggleActive(selected)}>
                  {selected.status !== false ? "Deactivate" : "Activate"}
                </Button>
              )}
              {canUpdate && (
                <Button size="sm" onClick={() => openEdit(selected)}>
                  Edit
                </Button>
              )}
            </div>
          </div>
        </aside>
      )}
      </div>

      {/* Dialog: import from CSV */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Import products</DialogTitle>
            <DialogDescription>Choose a CSV file with one product per row. Columns: {IMPORT_HELP}. Rows that cannot be added are listed afterwards.</DialogDescription>
          </DialogHeader>
          <input type="file" accept=".csv,text/csv" disabled={importing} onChange={(e) => runImport(e.target.files?.[0])} className="text-sm" />
          {importing && <p className="text-xs text-muted-foreground">Importing…</p>}
          {importResult && (
            <div className="space-y-1.5 text-sm">
              <p>
                <strong>{importResult.ok}</strong> {importResult.ok === 1 ? "product" : "products"} added{importResult.errors.length ? `, ${importResult.errors.length} skipped` : ""}.
              </p>
              {importResult.errors.slice(0, 8).map((er) => (
                <p key={er.row} className="text-xs text-danger">
                  Row {er.row}: {er.reason}
                </p>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Add / Edit */}
      <Dialog open={!!dialogItem} onOpenChange={(open) => !open && setDialogItem(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogItem === "new" ? "Add Product" : "Edit Product"}</DialogTitle>
            <DialogDescription>
              Prices are per unit and exclude GST.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-2 text-xs sm:text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs sm:text-sm">Product Name *</Label>
                <Input
                  required
                  placeholder="Annual Support Plan"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Code / SKU *</Label>
                <Input
                  required
                  placeholder="SUP-ANNUAL"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="font-mono uppercase"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Category</Label>
                <SearchableSelect
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className={selectClass}
                >
                  <option value="">-- Select Category --</option>
                  {masterCategories.map((c) => (
                    <option key={c.code} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">HSN / SAC Code</Label>
                <Input
                  placeholder="998313"
                  value={form.hsn_sac_code}
                  onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value })}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Selling Price (₹) *</Label>
                <Input
                  required
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Unit</Label>
                <SearchableSelect
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className={selectClass}
                  required
                >
                  <option value="">-- Select Unit of Measure --</option>
                  {masterUnits.map((u) => (
                    <option key={u.code} value={u.code}>
                      {u.name} ({u.code})
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">GST Rate</Label>
                <SearchableSelect
                  value={form.gst_rate}
                  onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })}
                  className={selectClass}
                >
                  {GST_RATES.map((r) => (
                    <option key={r} value={r}>
                      {r}%
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>

            {canViewCost && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">Cost Price (₹)</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Optional"
                    value={form.cost_price}
                    onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                  />
                </div>
                <div className="h-9 sm:h-10 flex items-center text-xs sm:text-sm text-muted-foreground">
                  {previewMargin === null ? (
                    "Margin is calculated from cost"
                  ) : (
                    <span>
                      Margin:{" "}
                      <strong className={previewMargin < 0 ? "text-destructive" : "text-foreground"}>
                        {previewMargin.toFixed(1)}%
                      </strong>
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Description</Label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-3.5 py-2.5">
              <div>
                <Label className="text-xs sm:text-sm">Active</Label>
                <p className="text-[11px] text-muted-foreground">
                  Inactive products can't be added to deals
                </p>
              </div>
              <Switch
                checked={form.status}
                onCheckedChange={(checked) => setForm({ ...form, status: checked })}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogItem(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-danger">Delete Product</DialogTitle>
            <DialogDescription>
              Remove <strong className="text-foreground">{deleteItem?.name}</strong>? If it is used on
              any deal it will be deactivated instead, so existing deals are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AttributesDialog open={attributesOpen} onClose={() => setAttributesOpen(false)} categories={categories} />
    </div>
  );
}
